import { useId, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  ChartTooltipContent,
  cn,
  TabsFluid,
  GappedChart,
} from "web-design-system";
import {
  Bar,
  BarChart,
  BarProps,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { FundPortfolio } from "creso-core/types";
import {
  ALLOCATION_PCT_THRESHOLD,
  BUCKET_LABELS,
  formatAsPercentage,
  titleCase,
  withRelativeBarPct,
  withScaledBarPct,
} from "creso-core/utils";
import { useFundHoldings } from "creso-core/hooks";
import { ChartBar } from "lucide-react";
import { ActiveShape } from "recharts/types/util/types";

const BUCKET_COLORS: Record<string, string> = {
  indian_equity: "var(--color-chart-1)",
  debt: "var(--color-chart-2)",
  commodity: "var(--color-chart-3)",
  global: "var(--color-chart-4)",
};

const MIN_BAR_WIDTH = 20;

export function MinWidthBar(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fill?: string;
  fillOpacity?: number;
  radius?: number;
}) {
  const { x = 0, y = 0, height = 0, fill, fillOpacity, radius = 0 } = props;
  const width = Math.max(props.width ?? 0, MIN_BAR_WIDTH);
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      fill={fill}
      fillOpacity={fillOpacity}
      rx={radius}
      ry={radius}
    />
  );
}

// Reuse this component in overview page.
export function AllocationBarRow(props: {
  label: string;
  pct: number;
  barPct: number;
  labelClassName?: string;
  color?: string;
  opacity?: number;
  gradient?: boolean;
  embedLabel?: boolean;
  enableAnimation?: boolean;
}) {
  const {
    label,
    pct,
    barPct,
    labelClassName,
    color,
    opacity,
    gradient,
    embedLabel,
    enableAnimation = true,
  } = props;
  const gradientId = useId();

  return (
    <div className="flex items-center gap-4">
      {!embedLabel && (
        <span className={cn("text-sm w-50 truncate shrink-0", labelClassName)}>
          {label}
        </span>
      )}
      <div className="relative flex-1 h-6">
        {embedLabel && (
          <span className="absolute inset-0 z-9 flex items-center pl-2 text-xs truncate pointer-events-none">
            {label}
          </span>
        )}
        {gradient ? (
          // Plain CSS bar instead of a recharts SVG: recharts'
          // ResponsiveContainer measures its size via ResizeObserver, which
          // is unreliable during print layout (bars render undersized and
          // misaligned), and CSS avoids that entirely.
          <div
            className={cn(
              "h-full rounded",
              !color &&
                "bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-600 dark:to-slate-700"
            )}
            style={{
              width: `${Math.max(barPct, 0)}%`,
              minWidth: MIN_BAR_WIDTH,
              ...(color && {
                background: `linear-gradient(to right, color-mix(in srgb, ${color} 5%, transparent), color-mix(in srgb, ${color} 20%, transparent))`,
              }),
            }}
          />
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={[{ name: label, value: Math.max(barPct, 0) }]}
              layout="vertical"
              barCategoryGap={0}
              margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
            >
              {!color && (
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
                    <stop
                      offset="0%"
                      className="[stop-color:var(--color-slate-100)] dark:[stop-color:var(--color-slate-600)]"
                    />
                    <stop
                      offset="100%"
                      className="[stop-color:var(--color-slate-200)] dark:[stop-color:var(--color-slate-700)]"
                    />
                  </linearGradient>
                </defs>
              )}
              <XAxis type="number" hide domain={[0, 100]} />
              <YAxis type="category" dataKey="name" hide />
              <Bar
                dataKey="value"
                radius={3}
                isAnimationActive={enableAnimation}
                animationDuration={450}
                animationEasing="ease-out"
                fill={color ?? `url(#${gradientId})`}
                fillOpacity={opacity}
                shape={MinWidthBar as ActiveShape<BarProps, SVGPathElement>}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
      <span className="text-sm text-muted-foreground min-w-12 text-right shrink-0">
        {formatAsPercentage(pct, "-", 1)}
      </span>
    </div>
  );
}

export function ExposureCard(props: {
  title: string;
  rows: { label: string; pct: number; barPct: number }[];
}) {
  const { title, rows } = props;

  return (
    <Card className="bg-background h-135 flex flex-col overflow-auto">
      <CardHeader className="p-5 z-10 sticky bg-background top-0">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 p-5 pt-0">
        <div className="flex flex-col flex-1 gap-4">
          {rows.map((row) => (
            <AllocationBarRow
              key={row.label}
              label={row.label}
              pct={row.pct}
              barPct={row.barPct}
              labelClassName="font-normal"
            />
          ))}
          {rows.length === 0 && (
            <div className="flex flex-col size-full justify-center gap-4 items-center">
              <ChartBar className="size-16 stroke-[0.75] text-muted-foreground" />
              <span className="text-sm text-muted-foreground">
                No data found.
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function FundAllocation(props: { portfolio: FundPortfolio }) {
  const { portfolio } = props;

  const buckets = (portfolio.allocation_breakdown?.buckets ?? [])
    .filter((bucket) => bucket.pct > ALLOCATION_PCT_THRESHOLD)
    .map((bucket) => ({
      ...bucket,
      label: BUCKET_LABELS[bucket.key] ?? bucket.label,
    }));

  const [activeBucketKey, setActiveBucketKey] = useState(() => buckets[0]?.key);
  const activeBucket =
    buckets.find((bucket) => bucket.key === activeBucketKey) ?? buckets[0];

  const allocationTabs = buckets.map((bucket) => ({
    value: bucket.key,
    title: (
      <div className="flex items-center gap-2">
        <span
          className="w-3 h-3 rounded shrink-0"
          style={{
            backgroundColor: BUCKET_COLORS[bucket.key],
          }}
        />
        <span className="font-semibold">{bucket.label}</span>
        <span className="text-muted-foreground">
          {formatAsPercentage(bucket.pct, "-", 1)}
        </span>
      </div>
    ),
    // Rendered outside the tab content below, so rows persist across tab
    // switches and recharts animates between values instead of remounting.
    body: null,
  }));

  const sectorExposure = useMemo(
    () =>
      withRelativeBarPct(
        Object.entries(portfolio.sector_allocation?.sectors ?? {}).map(
          ([k, v]) => ({
            label: titleCase(k?.replace("_", " ")),
            pct: v,
          })
        )
      ),
    [portfolio.sector_allocation?.sectors]
  );

  const activeSubBuckets = useMemo(
    () =>
      withScaledBarPct(
        (activeBucket?.sub_buckets ?? []).filter(
          (s) => s.pct > ALLOCATION_PCT_THRESHOLD
        )
      ),
    [activeBucket]
  );

  // Rank each row by pct (0 = highest) without sorting the rendered array,
  // so opacity steps stay evenly spaced even when pct values are close.
  const activeSubBucketRanks = useMemo(
    () =>
      activeSubBuckets.map(
        (subBucket) =>
          activeSubBuckets.filter((other) => other.pct > subBucket.pct).length
      ),
    [activeSubBuckets]
  );

  const { data: holdings } = useFundHoldings(portfolio.fund);

  const stocksExposure = useMemo(
    () =>
      withRelativeBarPct(
        (holdings ?? []).map((holding) => ({
          label: holding.name,
          pct: parseFloat(holding.weight ?? "0"),
        }))
      ),
    [holdings]
  );

  return (
    <div className="flex flex-col gap-8">
      {buckets.length > 0 && (
        <div className="relative flex flex-col gap-4">
          <h5 className="font-semibold">Asset Allocation</h5>
          <GappedChart
            segments={buckets.map((bucket) => ({
              key: bucket.key,
              value: bucket.pct,
              color: BUCKET_COLORS[bucket.key],
              label: bucket.label ?? bucket.key,
            }))}
            tooltipContent={
              <ChartTooltipContent
                indicator="dot"
                labelFormatter={() => "Asset Allocation"}
                valueFormatter={(value) => `${value}%`}
              />
            }
          />
          <div className="flex flex-col gap-px">
            <TabsFluid
              tabs={allocationTabs}
              value={activeBucketKey}
              onValueChange={setActiveBucketKey}
              contentClassName="hidden"
            />
            {activeBucket && (
              <div
                className={cn(
                  "-mt-px flex flex-col gap-4 rounded-lg border border-border p-4",
                  activeBucket.key === buckets[0]?.key && "rounded-tl-none"
                )}
              >
                <span className="text-sm text-muted-foreground">
                  Breakdown by{" "}
                  <span className="text-foreground font-medium">
                    {activeBucket.breakdown_by}
                  </span>
                </span>
                {activeSubBuckets.map((subBucket, index) => (
                  <AllocationBarRow
                    key={index}
                    label={subBucket.label}
                    pct={subBucket.pct}
                    barPct={subBucket.barPct}
                    color={BUCKET_COLORS[activeBucket.key]}
                    opacity={
                      activeSubBuckets.length > 1
                        ? 1 -
                          (activeSubBucketRanks[index] /
                            (activeSubBuckets.length - 1)) *
                            0.5
                        : 1
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
      {(sectorExposure.length > 0 || stocksExposure.length > 0) && (
        <div className="flex flex-col gap-4">
          <h5 className="font-semibold">Top holdings</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <ExposureCard title="Stocks Exposure" rows={stocksExposure} />
            <ExposureCard title="Sector Exposure" rows={sectorExposure} />
          </div>
        </div>
      )}
    </div>
  );
}
